import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useState } from "react";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { TablePopUp, TopDisplay } from "../../../../components";
import {
  modalAtom,
  notificationAtom,
  openAtom,
  openNAtom,
} from "../../../../context";
import apiCall from "../../../../utils/apiCall";
import { TopLink } from "../";

type ExamDuePayment = {
  MembershipNo: string;
  Amount: number;
  OutstandingAmount: number;
  RouteId: number;
  RoutePayableActivitiesId: number;
  ActionId: number;
  DueYear: string;
};

const HeadStyle = {
  borderBottom: "1px solid var(--dark-green-color)",
  padding: "15px 5px",
};

const DataStyle = {
  padding: "5px 5px",
};

const ROWSTYLE = styled.tr`
  & > td:last-of-type > * {
    display: none;
  }
  :hover > td {
    background-color: var(--table-highlight);
    &:last-of-type > * {
      display: block;
    }
  }
  &.active > td:last-of-type > * {
    display: block !important;
  }
`;

const DeleteSubscriptionAtom = atom<number | null>(null);

export default function DeleteSubscription() {
  const [, set_modal] = useAtom(modalAtom);
  const [, set_open] = useAtom(openAtom);
  const [active] = useAtom(DeleteSubscriptionAtom);
  const [subscriptions, setSubscriptions] = useState<ExamDuePayment[]>([]);
  const [studentNumber, setStudentNumber] = useState("");

  const handleCheckSubscription = () => {
    set_modal({
      header: "Check Student Subscriptions",
      content: (
        <CheckStudentSubscription
          onSuccess={(data, membershipNo) => {
            setSubscriptions(data);
            setStudentNumber(membershipNo);
          }}
        />
      ),
    });
    set_open(true);
  };

  const handleDeleteSuccess = (deletedSubscription: ExamDuePayment) => {
    setSubscriptions((prev) =>
      prev.filter(
        (item) =>
          !(
            item.RoutePayableActivitiesId ===
              deletedSubscription.RoutePayableActivitiesId &&
            item.DueYear === deletedSubscription.DueYear
          )
      )
    );
  };

  return (
    <Box>
      <TopLink />
      <TopDisplay
        label="Delete Subscription"
        button_array={[
          <Button
            onClick={handleCheckSubscription}
            bg="var(--dark-green-color)"
            color="var(--white-color)"
            type="button"
          >
            Check Student Subscriptions
          </Button>,
        ]}
      />

      <Flex mt="25px" flexDir="column" gap="10px">
        <Text fontSize="sm" mt="10px">
          {studentNumber
            ? `Showing ${subscriptions.length} subscription(s) for ${studentNumber}`
            : "Click the button above to check a student's subscriptions"}
        </Text>
      </Flex>

      {subscriptions.length > 0 ? (
        <TableContainer mt="20px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th sx={HeadStyle}>Membership No</Th>
                <Th sx={HeadStyle}>Amount</Th>
                <Th sx={HeadStyle}>Outstanding Amount</Th>
                <Th sx={HeadStyle}>Route Id</Th>
                <Th sx={HeadStyle}>Route Payable Activities Id</Th>
                <Th sx={HeadStyle}>Action Id</Th>
                <Th sx={HeadStyle}>Due Year</Th>
                <Th sx={HeadStyle} w="50px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {subscriptions.map((subscription) => (
                <ROWSTYLE
                  key={`${subscription.MembershipNo}-${subscription.RoutePayableActivitiesId}-${subscription.DueYear}`}
                  className={
                    subscription.RoutePayableActivitiesId === active
                      ? "active"
                      : ""
                  }
                >
                  <Td sx={DataStyle}>{subscription.MembershipNo}</Td>
                  <Td sx={DataStyle}>{subscription.Amount}</Td>
                  <Td sx={DataStyle}>{subscription.OutstandingAmount}</Td>
                  <Td sx={DataStyle}>{subscription.RouteId}</Td>
                  <Td sx={DataStyle}>
                    {subscription.RoutePayableActivitiesId}
                  </Td>
                  <Td sx={DataStyle}>{subscription.ActionId}</Td>
                  <Td sx={DataStyle}>{subscription.DueYear}</Td>
                  <Td sx={DataStyle} w="50px" h="50px">
                    <DeleteSubscriptionPopUp
                      data={subscription}
                      onDeleteSuccess={handleDeleteSuccess}
                    />
                  </Td>
                </ROWSTYLE>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Text mt="20px">No Subscription Found</Text>
      )}
    </Box>
  );
}

const CheckStudentSubscriptionSchema = Yup.object({
  studentNumber: Yup.string().required("Student number is required"),
});

const CheckStudentSubscription = (props: {
  onSuccess: (data: ExamDuePayment[], studentNumber: string) => void;
}) => {
  const [, set_open] = useAtom(openAtom);
  const [, set_Nopen] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const { mutate: checkSubscriptions, isLoading } = useMutation({
    mutationKey: ["check-student-subscriptions"],
    mutationFn: async (studentNumber: string) =>
      apiCall().get(
        `/Payment/GetCurrentExamDuePaymentAdmin?RouteId=1&MembershipNo=${encodeURIComponent(
          studentNumber
        )}`
      ),
    onSuccess(response: any, membershipNo: string) {
      const data = Array.isArray(response?.data?.Data) ? response.data.Data : [];

      props.onSuccess(data, membershipNo);
      set_notification({
        type: "success",
        message: response?.data?.Message || "Student subscriptions fetched",
      });
      set_Nopen(true);
      set_open(false);
    },
    onError(error: any) {
      set_notification({
        type: "error",
        message:
          error?.response?.data?.Message ||
          error?.data?.Message ||
          "Error occurred",
      });
      set_Nopen(true);
    },
  });

  return (
    <Formik
      initialValues={{ studentNumber: "" }}
      validationSchema={CheckStudentSubscriptionSchema}
      onSubmit={(values) => {
        checkSubscriptions(values.studentNumber.trim());
      }}
    >
      {({ errors, touched, values, handleChange }) => (
        <Form>
          <Flex flexDir="column" gap="20px" minW="400px">
            <Box>
              <label>Student Number</label>
              <Input
                name="studentNumber"
                value={values.studentNumber}
                onChange={handleChange}
                placeholder="Enter student number"
                borderColor="green"
              />
              {errors.studentNumber && touched.studentNumber && (
                <Text color="red.500" fontSize="sm">
                  {errors.studentNumber}
                </Text>
              )}
            </Box>

            <Button
              type="submit"
              isDisabled={isLoading}
              bg="var(--dark-green-color)"
              color="white"
            >
              {isLoading ? "Loading..." : "Submit"}
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

const DeleteSubscriptionPopUp = (props: {
  data: ExamDuePayment;
  onDeleteSuccess: (data: ExamDuePayment) => void;
}) => {
  const { data, onDeleteSuccess } = props;
  const [active, set_active] = useAtom(DeleteSubscriptionAtom);
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);
  const [, set_Nopen] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const { mutate: deleteSubscription, isLoading } = useMutation({
    mutationKey: ["delete-student-subscription"],
    mutationFn: async () =>
      apiCall().delete("/Payment/DeleteExamDue", {
        data: {
          StudentNo: data.MembershipNo,
          RoutePayableActivitiesId: data.RoutePayableActivitiesId,
          DueYear: Number(data.DueYear),
        },
      }),
    onSuccess(response: any) {
      onDeleteSuccess(data);
      set_notification({
        type: "success",
        message: response?.data?.Message || "Subscription deleted successfully",
      });
      set_Nopen(true);
    },
    onError(error: any) {
      set_notification({
        type: "error",
        message:
          error?.response?.data?.Message ||
          error?.data?.Message ||
          "Error occurred",
      });
      set_Nopen(true);
    },
    onSettled() {
      set_open(false);
      set_active(null);
    },
  });

  const handleClick = () => {
    if (data.RoutePayableActivitiesId === active) {
      set_active(null);
      return;
    }

    set_active(data.RoutePayableActivitiesId);
  };

  const handleDelete = () => {
    set_modal({
      header: "Delete Subscription",
      content: (
        <Grid gap="20px">
          <GridItem>
            Are you sure you want to delete this subscription?
          </GridItem>
          <GridItem as={Flex} justify="space-between" p="20px 0" gap="20px">
            <Button
              isDisabled={isLoading}
              onClick={() => deleteSubscription()}
              bg="var(--dark-green-color)"
              color="var(--white-color)"
            >
              {isLoading ? "Loading" : "Delete"}
            </Button>
            <Button
              isDisabled={isLoading}
              variant="outline"
              bg="var(--white-color)"
              onClick={() => set_open(false)}
              border="1px solid var(--dark-green-color)"
              color="var(--dark-green-color)"
            >
              Cancel
            </Button>
          </GridItem>
        </Grid>
      ),
    });
    set_open(true);
  };

  return (
    <TablePopUp
      click={handleClick}
      options={[{ label: "Delete", click: handleDelete }]}
    />
  );
};
